
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    Checkbox,
    IconButton,
    Tooltip,
    Dialog,             
    DialogTitle,         
    DialogContent,       
    DialogContentText,   
    DialogActions,       
    Button
} from '@mui/material';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import AdTechVendorMappingsLookup from '../Lookups/AdTechVendorMappingsLookup';
import AddIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { toast } from 'react-toastify';
import { httpGet, httpDelete, httpPost } from '../../HttpRequestHandling/httpRequestHandler';
import { handleTestConnection } from './common';


const LinkIntegration = (props) => {

    const location = useLocation();
    const navigate = useNavigate();
    const rowToBeUpdated = location?.state?.rowToBeUpdated ?? null;
    const mode = rowToBeUpdated ? 'edit' : 'new';

    const userInfo = JSON.parse(localStorage.userInfo);
    const createId = userInfo?.id ?? '';
    const routeCompanyCode = rowToBeUpdated?.companyCode ?? null;
    const effectiveCompanyCode = routeCompanyCode ?? userInfo?.companyCode ?? null;

    const integrationIdFromRoute = rowToBeUpdated?.integrationId ?? null;
    const adtechIntegrationIdFromRoute = rowToBeUpdated?.adtechIntegrationId ?? null;


    const [ipcOptions, setIpcOptions] = useState([]); 
    const [selectedAdtechId, setSelectedAdtechId] = useState(''); 

    const [errors, setErrors] = useState({});

    const [formValues, setFormValues] = useState({
     
        name: '',
        accountId: '',
        userId: '',
        password: '',
        accessTag: '',
        connectionStatus: false, 
        isPdfRead: false
    });

    const [editorIntegration, setEditorIntegration] = useState(null);       
    const [editorIntegrationAdtech, setEditorIntegrationAdtech] = useState(null); 

    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [openVendorLookup, setOpenVendorLookup] = useState(false);

    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [deleteRowData, setDeleteRowData] = useState(null);

    const handleInputChange = (field) => (e) => {
        setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            { label: 'Name', fieldName: 'name' },
            { label: 'Account ID', fieldName: 'accountId' },
            { label: 'User ID', fieldName: 'userId' },
            { label: 'Password', fieldName: 'password' },
            { label: 'Access Tag', fieldName: 'accessTag' },
        ];

        requiredFields.forEach(({ label, fieldName }) => {
            const val = (formValues[fieldName] || '').trim();
            if (!val) newErrors[fieldName] = `${label} is required`;
        });

        if (!String(selectedAdtechId || '').trim()) {
            newErrors.integration = 'Integration is required';
        }
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            toast.error('Please enter all required fields.');
            return false;
        }
        return true;
    };


    useEffect(() => {
        let mounted = true;
        props.inProgress?.(true);

        httpGet('/api/integrations/ipcintegrations')
            .then((resp) => {
                if (!mounted) return;
                const list = Array.isArray(resp?.data) ? resp.data : [];
                setIpcOptions(list);

                if (
                    mode === 'edit' &&
                    effectiveCompanyCode &&
                    integrationIdFromRoute != null &&
                    adtechIntegrationIdFromRoute != null
                ) {
                    loadEditorData(list);
                }
            })
            .catch((err) => {
                toast.error("Failed to load IPC integrations.")
            })
            .finally(() => {
                props.inProgress?.(false);
            });

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {

        if (mode !== 'new') return;

        const selected = ipcOptions.find(o => String(o.toolID) === String(selectedAdtechId));
        const name = (selected?.ipcToolName || '').toString();

        const shouldReadPdf = /(facebook|meta)/i.test(name);

        setFormValues(prev =>
            prev.isPdfRead === shouldReadPdf ? prev : { ...prev, isPdfRead: shouldReadPdf }
        );
    }, [mode, selectedAdtechId, ipcOptions]);

    const loadEditorData = (ipcList) => {
        props.inProgress?.(true);
        const url = `/api/integrations/adtech/mappingeditor/${effectiveCompanyCode}/${integrationIdFromRoute}/${adtechIntegrationIdFromRoute}`;

        httpGet(url)
            .then(({ data = {} }) => {
                const integ = data.integration || {};
                const integAd = data.integrationAdtech || {};
                const vendorMappings = Array.isArray(data.vendorMapping) ? data.vendorMapping : [];

                if (integAd.adToolId != null) {
                    const match = ipcList.find((o) => String(o.toolID) === String(integAd.adToolId));
                    if (match) setSelectedAdtechId(String(match.toolID));
                }

                const isActiveBool = integAd.isActive;
                const isPdfRead = integAd.isPdfRead;


                setFormValues((prev) => ({
                    ...prev,
                    name: integAd.adtechIntegrationName || '',
                    accountId: integAd.accountId || '',
                    userId: integAd.userId || '',
                    password: integAd.password || '',
                    accessTag: integAd.accessTag || '',
                    connectionStatus: isActiveBool,
                    isPdfRead
                }));

                setRows(
                    vendorMappings.map((v, i) => ({
                        id: v.vendorMappingId ?? i + 1,
                        vendorMappingId: v.vendorMappingId ?? i + 1,
                        modelName: v.modelName || '',
                        modelTag: v.modelTag || '',
                        erpVendor: v.erpVendor || '',
                        currencyCode: v.currencyCode || '',
                        vendorLinkId: v.vendorLinkId || '',
                        activeFlag: Number(v.activeFlag ?? 0),
                    }))
                );

                setEditorIntegration({
                    IntegrationId: Number(integ.integrationId || 0),
                    IntegrationName: integ.integrationName || '',
                    IntegrationTypeCode: integ.integrationTypeCode || '',
                    IntegrationStatusCode: integ.integrationStatusCode || '',
                    IntegrationStatus: integ.integrationStatus || '',
                });

                setEditorIntegrationAdtech({
                    AdtechIntegrationId: Number(integAd.adtechIntegrationId || 0),
                    AdtechIntegrationName: integAd.adtechIntegrationName || '',
                    AccountId: integAd.accountId || '',
                    AdToolId: Number(integAd.adToolId || 0),
                    AdToolSeqId: integAd.adToolSeqId ?? null,
                    IsActive: integAd.isActive,
                    IsPdfRead: integAd.isPdfRead,
                    AccessTag: integAd.accessTag || '',
                    UserId: integAd.userId || '',
                    Password: integAd.password || '',
                });
            })
            .catch(() => {
                toast.error('Failed to load integration details');
            })
            .finally(() => {
                props.inProgress?.(false);
            
            });
    };


    const selectedLabel = useMemo(() => {
        const found = ipcOptions.find((o) => String(o?.toolID) === String(selectedAdtechId));
        return found?.ipcToolName || '';
    }, [ipcOptions, selectedAdtechId]);

    const handleAddClicked = () => setOpenVendorLookup(true);

    const handleVendorLookupClose = (selectedRows) => {
        setOpenVendorLookup(false);
        if (!Array.isArray(selectedRows) || selectedRows.length === 0) return;

        const existingIds = new Set(rows.map(r => String(r.vendorMappingId)));
        const existingCurrencies = new Set(rows.map(r => (r.currencyCode || '').toUpperCase()));
        const additions = [];

        for (const r of selectedRows) {
            const vid = String(r.vendorMappingId);
            const cur = (r.currencyCode || '').toUpperCase();

            if (existingIds.has(vid)) continue;
            
            additions.push({
                id: r.vendorMappingId,
                vendorMappingId: r.vendorMappingId,
                modelName: r.modelName || '',
                modelTag: r.modelTag || '',
                erpVendor: r.erpVendor || '',
                currencyCode: r.currencyCode || '',
                activeFlag: 1
            });
            existingIds.add(vid);
            existingCurrencies.add(cur);
        }

        if (additions.length) setRows(prev => [...prev, ...additions]);
    };

    const handleRequestDeleteSelected = () => {
        if (!selectedRows || selectedRows.length === 0) {
            toast.error("Select at least one row to delete.");
            return;
        }
        setDeleteRowData(selectedRows.slice()); 
        setOpenDeleteConfirmation(true);
    };

    const confirmDelete = async () => {
        if (!deleteRowData || deleteRowData.length === 0) {
            setOpenDeleteConfirmation(false);
            return;
        }

        const localOnly = deleteRowData.filter(r => !r?.vendorLinkId);
        const persisted = deleteRowData.filter(r => r?.vendorLinkId);

        if (localOnly.length) {
            const localIds = new Set(localOnly.map(r => String(r.id)));
            setRows(prev => prev.filter(r => !localIds.has(String(r.id))));
        }

        if (mode === 'new' || !effectiveCompanyCode || !adtechIntegrationIdFromRoute || persisted.length === 0) {
            setSelectedRows([]);
            setOpenDeleteConfirmation(false);
            setDeleteRowData(null);
            if (localOnly.length) {
                toast.success(`Vendor mapping${localOnly.length > 1 ? 's' : ''} removed.`);
            }
            return;
        }


        const vendorLinkIdsCsv = persisted.map(r => String(r.vendorLinkId)).join(',');

        props.inProgress?.(true);
        try {
            const url = `/api/integrations/vendorlinkintegrationmapping/${effectiveCompanyCode}/${adtechIntegrationIdFromRoute}?vendorLinkId=${encodeURIComponent(vendorLinkIdsCsv)}`;
            const resp = await httpDelete(url);

            const ok = resp?.data === true;
            if (ok) {
                const persistedIds = new Set(persisted.map(r => String(r.id)));
                setRows(prev => prev.filter(r => !persistedIds.has(String(r.id))));
                setSelectedRows([]);
                toast.success(`Vendor mapping${persisted.length > 1 ? 's' : ''} deleted.`);
            } else {
                toast.error('Delete failed.');
            }
        } catch {
            toast.error('An error occurred while deleting mappings.');
        } finally {
            props.inProgress?.(false);
            setOpenDeleteConfirmation(false);
            setDeleteRowData(null);
        }
    };



    const closeDeleteDialog = () => {
        setOpenDeleteConfirmation(false);
        setDeleteRowData(null);
    };


    const handleSave = async () => {

        if (!validateForm()) return; 

        if (!effectiveCompanyCode) {
            return;
        }

        const selectedIpc =
            ipcOptions.find((o) => String(o.toolID) === String(selectedAdtechId)) || null;

        let Integration;
        if (mode === 'new') {
            if (!selectedIpc) {
                toast.error("Please select an AdTech Integration.")
                return;
            }
            Integration = {
                IntegrationId: 0,
                IntegrationName: selectedIpc.ipcToolName ?? '',
                IntegrationTypeCode: 'INTAT01',
                IntegrationStatusCode: 'INTAT01',
                IntegrationStatus: selectedIpc?.isConnectorActive ? 'Y' : 'N',
            };
        } else {
            Integration = {
                IntegrationId: Number(editorIntegration?.IntegrationId ?? 0),
                IntegrationName: editorIntegration?.IntegrationName ?? '',
                IntegrationTypeCode: editorIntegration?.IntegrationTypeCode ?? '',
                IntegrationStatusCode: editorIntegration?.IntegrationStatusCode ?? '',
                IntegrationStatus: editorIntegration?.IntegrationStatus ?? '',
            };
        }

        let IntegrationAdtech;
        if (mode === 'new') {
            IntegrationAdtech = {
                AdtechIntegrationId: 0,
                AdtechIntegrationName: formValues.name ?? '',
                AccountId: formValues.accountId ?? '',
                AdToolId: Number(selectedIpc?.toolID ?? 0),
                AdToolSeqId: Number(selectedIpc?.sequenceID ?? 0),
                IsActive: formValues.connectionStatus ? 1 : 0,
                IsPdfRead: formValues.isPdfRead,
                AccessTag: formValues.accessTag ?? '',
                UserId: formValues.userId ?? '',
                Password: formValues.password ?? '',
            };
        } else {
            IntegrationAdtech = {
                AdtechIntegrationId: Number(editorIntegrationAdtech?.AdtechIntegrationId ?? 0),
                AdtechIntegrationName: formValues.name ?? '',
                AccountId: formValues.accountId ?? '',
                AdToolId: Number(editorIntegrationAdtech?.AdToolId ?? 0),
                AdToolSeqId: Number(editorIntegrationAdtech?.AdToolSeqId ?? 0),
                IsActive: formValues.connectionStatus,
                IsPdfRead: formValues.isPdfRead,
                AccessTag: formValues.accessTag ?? '',
                UserId: formValues.userId ?? '',
                Password: formValues.password ?? '',
            };
        }


        const VendorMapping = rows.map((r) => ({
           
            VendorMappingId: r.vendorMappingId ?? '',
            ModelName: r.modelName ?? '',
            ModelTag: r.modelTag ?? '',
            ErpVendor: r.erpVendor ?? '',
            CurrencyCode: r.currencyCode ?? ''

        }));

        const body = {
            CompanyCode: Number(effectiveCompanyCode),
            CreateId: String(createId ?? ''),
            Data: {
                Integration,
                IntegrationAdtech,
                VendorMapping,
            },
        };

        props.inProgress?.(true);
        try {
            const resp = await httpPost('/api/integrations/adtech/mappingeditor', body);
            const ok = resp?.data;
            if (ok.errorCode === 1) {
                let errorMsg = ok?.errorMessage || `Integration has been ${mode === 'new' ? 'added' : 'updated'} successfully.`;
                toast.success(errorMsg);
                props.inProgress?.(false);
                navigate('/Integrations');
            } else {
                toast.error(<div><div>Failed to save integration.</div><div>{ok?.errorMessage || ''}</div></div>);
                props.inProgress?.(false);

            }
        } catch (err) {
            toast.error("Failed to save integration.");
            props.inProgress?.(false);
        }
    };
    const testConnection_onClick = async () => {
        const payload = {
            password: formValues.password,
            IpcDevToken: "", // optional, can be left blank
            userId: formValues.userId,
            accountId: formValues.accountId,
            accessTag: formValues.accessTag,
            adToolId: selectedAdtechId
        };
        const callback = (response) => {
            setFormValues({ ...formValues, connectionStatus: response });
        }
        const progress = props.inProgress;

        await handleTestConnection(payload, callback, progress);
    }

    return (
        <Box>
            <h1>{mode === 'new' ? 'Link New Integration' : 'Update Integration'}</h1>

            <Box display="flex" flexDirection="column" gap={2} ml={1} maxWidth={500} sx={{ mb: 5, mt:7 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography sx={{ minWidth: 100 }}><span style={{ color: 'red' }}> *</span>Name:</Typography>
                    <TextField
                        
                        variant="outlined"
                        size="small"
                        value={formValues.name}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            // Allow only letters, numbers, underscore, hyphen, and space
                            const filtered = inputValue.replace(/[^a-zA-Z0-9_\- ]/g, "");
                            handleInputChange('name')({ ...e, target: { ...e.target, value: filtered }});
                        }}
                        sx={{ minWidth: 100, width: 335 }} 
                        placeholder="Enter Integration Name"
                        error={!!errors.name}  
                        inputProps={{ maxLength: 64 }}
                    />
                </Box>
                <Box display="flex" gap={2} maxWidth={1000}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography sx={{ minWidth: 100, fontSize: '17px' }}><span style={{ color: 'red' }}> *</span>Integration:</Typography>
                        <Select
                            value={selectedAdtechId || ''}
                            onChange={(e) => setSelectedAdtechId(e.target.value)}
                            disabled={mode === 'edit'}
                            sx={{ minWidth: 100, width: 335, border: errors.integration ? '1px solid red' : undefined, }}
                            size="small"
                            displayEmpty
                        >
                            <MenuItem value="">Select an AdTech Integration</MenuItem>
                            {ipcOptions.map((opt) => (
                                <MenuItem key={opt.toolID} value={String(opt.toolID)}>
                                    {opt.ipcToolName}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <Checkbox
                            checked={!!formValues.isPdfRead}
                            disabled
                        />
                        <Typography sx={{ minWidth: 100 }}>Read PDF</Typography>
                    </Box>

                </Box>
                
            </Box>

            <Typography variant="h6" gutterBottom color="primary" sx={{ ml: 1 }}>
                <b>{selectedLabel || 'AdTech Integration'}</b>
            </Typography>


            <Box display="flex" flexDirection="column" gap={2} ml={1} maxWidth={1100}>
               
                <Box display="flex" gap={25}>
                    <Box flex={1} display="flex" alignItems="center" gap={2}>
                        <Typography sx={{ minWidth: 100 }}><span style={{ color: 'red' }}> *</span>Account Id:</Typography>
                        <TextField
                            variant="outlined"
                            size="small"
                            value={formValues.accountId}
                            onChange={handleInputChange('accountId')}
                            placeholder="Enter Account Id"
                            error={!!errors.accountId}  
                            fullWidth
                        />
                    </Box>
                    <Box flex={1} display="flex" alignItems="center" gap={2}>
                        <Typography sx={{ minWidth: 100 }}><span style={{ color: 'red' }}> *</span>User Id:</Typography>
                        <TextField
                            variant="outlined"
                            size="small"
                            value={formValues.userId}
                            onChange={handleInputChange('userId')}
                            placeholder="Enter User Id"
                            error={!!errors.userId}  
                            fullWidth
                        />
                    </Box>
                </Box>

                <Box display="flex" gap={25}>
                    <Box flex={1} display="flex" alignItems="center" gap={2}>
                        <Typography sx={{ minWidth: 100 }}><span style={{ color: 'red' }}> *</span>Password:</Typography>
                        <TextField
                            variant="outlined"
                            size="small"
                            type="password"
                            value={formValues.password}
                            onChange={handleInputChange('password')}
                            placeholder="Enter Password"
                            error={!!errors.password}  
                            fullWidth
                        />
                    </Box>
                    <Box flex={1} display="flex" alignItems="center" gap={2}>
                        <Typography sx={{ minWidth: 100 }}><span style={{ color: 'red' }}> *</span>Access Tag:</Typography>
                        <TextField
                            variant="outlined"
                            size="small"
                            value={formValues.accessTag}
                            onChange={handleInputChange('accessTag')}
                            placeholder="Enter Access Tag"
                            error={!!errors.accessTag}  
                            fullWidth
                        />
                    </Box>
                </Box>

                <Box display="flex" gap={5}>
                    <Box display="flex" alignItems="center">
                        <Typography sx={{ minWidth: 102 }}>
                            Active
                        </Typography>
                        <Checkbox
                            checked={!!formValues.connectionStatus}
                            onChange={(e) =>
                                setFormValues((prev) => ({
                                    ...prev,
                                    connectionStatus: e.target.checked,
                                }))
                            }
                        />
                    </Box>
                </Box>


                
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2} sx={{ width: '100%' }}>
                <button className="btn-blue" onClick={handleSave}>
                    Save
                </button>
                <button className="btn-blue" onClick={testConnection_onClick}>
                    Test Connection
                </button>
            </Box>

            <Box mb={2} mx={1}>
                <Typography variant="h6" gutterBottom color="primary">
                    <b>Link Vendor Mapping</b>
                </Typography>

                <EnhancedDataGrid
                    rows={rows}
                    columns={[
                       
                        { headerName: 'ID', field: 'vendorMappingId', flex: 1, minWidth: 120 },
                        { headerName: 'Model Name', field: 'modelName', flex: 1, minWidth: 160 },
                        { headerName: 'Model Tag', field: 'modelTag', flex: 1, minWidth: 200 },
                        { headerName: 'ERP Vendor', field: 'erpVendor', flex: 1, minWidth: 200 },
                        { headerName: 'Currency Code', field: 'currencyCode', flex: 1, minWidth: 140 },
                        { headerName: 'Active',field: 'activeFlag', flex: 1, minWidth: 100,
                            valueGetter: (params) => {
                                const v = params.row?.activeFlag;
                                return Number(v) === 1 || v === true || v === '1' ? 1 : 0;
                            },
                            valueFormatter: (params) => (Number(params.value) === 1 ? 'Yes' : 'No'),
                        }

                    ]}
                    getRowId={(row) => row.id}
                    options={{
                        selection: true,
                        showSelectAllCheckbox: false,
                        showTextRowsSelected: false,
                    }}
                    onSelectionChange={(rows) => {
                        setSelectedRows(rows || []);
                    }}
                    pageSize={5}
                    disableSelectionOnClick
                    editable={{
                        onRowUpdate: (newData, oldData) =>
                            new Promise((resolve) => {
                                setRows((prev) => prev.map((r) => (r.id === oldData.id ? newData : r)));
                                resolve(newData);
                            }),
                    }}
                    sx={{
                        height:'400px',
                        '& .MuiDataGrid-columnHeaderTitle': { fontSize: '13px'},
                        '& .MuiDataGrid-cell': { fontSize: '13px' },
                    }}
                    //leftCustomComponent={
                        //<Box sx={{ marginLeft:'15px' ,display: 'flex', alignItems: 'center', gap: 1, width: '300px' }}>
                            //<Typography sx={{ fontWeight: 700 }} color="primary">Options:</Typography>
                            //<Box display="flex" alignItems="center">
                                //<Checkbox
                                   // size="small"
                                   // checked={!!formValues.isPdfRead}
                                    //onChange={(e) => setFormValues((prev) => ({ ...prev, isPdfRead: e.target.checked }))}
                                ///>
                                //<Typography sx={{ fontWeight: 500, ml: 0.5, fontSize:'14px' }}>Read PDF</Typography>
                           // </Box>
                        //</Box>
                    //}
                    customcomponent={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 1 }}>
                            <Tooltip title="Add">
                                <IconButton size="small" onClick={handleAddClicked}>
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete selected">
                                <IconButton size="small" disabled={selectedRows.length === 0} onClick={handleRequestDeleteSelected}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                />
            </Box>
            <AdTechVendorMappingsLookup
                open={openVendorLookup}
                onClose={handleVendorLookupClose}
                preselectedIds={rows.map(r => r.vendorMappingId)}
                existingCurrencyCodes={rows.map(r => r.currencyCode)}
                modelDefId={1} 
                inProgress={props.inProgress}
            />

            <Dialog open={openDeleteConfirmation} aria-labelledby="draggable-dialog-title">
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    Delete Mapping{deleteRowData && deleteRowData.length > 1 ? 's' : ''}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteRowData && deleteRowData.length > 1
                            ? `Are you sure you want to delete selected vendor mappings?`
                            : <>Are you sure you want to delete vendor mapping "
                                {deleteRowData && deleteRowData[0]
                                    ? (deleteRowData[0].modelName + '' + deleteRowData[0].currencyCode)
                                    : ''}
                                "?</>
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={confirmDelete} color="primary">Yes</Button>
                    <Button autoFocus onClick={closeDeleteDialog} color="primary">No</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default LinkIntegration;



